import { BannerLayer, bannerClass } from './Banner';
import { ensureFont, firstFamily } from './fonts';
import { NAME_FONTS, NAME_FONT_SCALE, achievementById, type Player } from '@ofc/shared';
import { Avatar, plateStyle } from './Avatar';

/** Asks the app to show somebody's player card. */
export const PLAYER_CARD_EVENT = 'ofc:player-card';

export interface PlayerCardRequest {
  userId: string;
  name: string;
  colour: string;
}

export function openPlayerCard(player: Pick<Player, 'userId' | 'name' | 'color'>): void {
  if (!player.userId) return;
  window.dispatchEvent(
    new CustomEvent<PlayerCardRequest>(PLAYER_CARD_EVENT, {
      detail: { userId: player.userId, name: player.name, colour: player.color },
    }),
  );
}

/** The style for a name in the typeface its owner picked. */
export function nameStyle(font: string | undefined, size = 16): React.CSSProperties {
  if (!font || font === 'house' || !NAME_FONTS[font]) return {};
  // Fetched the first time anybody on screen wears it.
  ensureFont(firstFamily(NAME_FONTS[font]));
  return {
    fontFamily: NAME_FONTS[font],
    fontSize: Math.round(size * (NAME_FONT_SCALE[font] ?? 1)),
    letterSpacing: font === 'sign' ? '0.02em' : undefined,
  };
}

/**
 * One player in a lobby list, at the size the Movie Game's list set.
 *
 * The other lobbies used a slim two-pixel strip, which was too thin to show off
 * the plate a player picked, so every lobby now uses this one card. The Movie
 * Game keeps its own row for dragging the turn order around, but matches this
 * card's size.
 */
export function PlayerCard({
  player,
  you,
  hostId,
  onKick,
}: {
  player: Player;
  you: string | null;
  hostId: string;
  /** Given only to the host, and only for other players. */
  onKick?: () => void;
}) {
  // Signed-in players have a card worth opening; a guest has nothing to show.
  const tappable = Boolean(player.userId);

  return (
    <li
      className={`panel row entrance-${player.entrance ?? 'fade'}${bannerClass(player.banner)}`}
      onClick={tappable ? () => openPlayerCard(player) : undefined}
      onKeyDown={
        tappable
          ? (event) => {
              if (event.key === 'Enter' || event.key === ' ') openPlayerCard(player);
            }
          : undefined
      }
      role={tappable ? 'button' : undefined}
      tabIndex={tappable ? 0 : undefined}
      aria-label={tappable ? `${player.name}: open their card` : undefined}
      style={{
        cursor: tappable ? 'pointer' : undefined,
        padding: '8px 10px',
        gap: 10,
        // Every card the same height, whether or not it carries a remove button.
        minHeight: 58,
        opacity: player.connected ? 1 : 0.5,
        ...plateStyle(player.plate, player.color),
      }}
    >
      <BannerLayer banner={player.banner} colour={player.color} strip />
      <Avatar player={player} size={26} />
      <span className="grow col" style={{ gap: 0, minWidth: 0 }}>
        <span className="truncate" style={{ color: player.color }}>
          <span style={nameStyle(player.font)}>{player.name}</span>
          {player.id === you && <span className="muted small"> (you)</span>}
          {player.id === hostId && <span className="muted small"> · host</span>}
          {!player.connected && <span className="muted small"> · away</span>}
          {player.connected && player.sittingOut && <span className="muted small"> · 💤 sitting out</span>}
        </span>
        <PlayerTitle title={player.title} colour={player.color} />
      </span>
      {onKick && (
        <button
          className="btn-ghost small"
          aria-label={`Remove ${player.name}`}
          onClick={(event) => {
            // Removing someone must not also open their card.
            event.stopPropagation();
            onKick();
          }}
          style={{ minHeight: 36, minWidth: 36, padding: '2px 10px', border: 0, color: 'var(--chrome)' }}
        >
          ✕
        </button>
      )}
    </li>
  );
}

/**
 * An earned title, in small caps under a name. Renders nothing for a player
 * without one, or for a title the catalogue no longer knows.
 */
export function PlayerTitle({ title, colour }: { title?: string | null; colour: string }) {
  const name = title ? achievementById(title)?.name : undefined;
  if (!name) return null;
  return (
    <span
      className="sign truncate"
      style={{ color: colour, opacity: 0.75, fontSize: 11, letterSpacing: '0.14em', lineHeight: 1.3 }}
    >
      {name}
    </span>
  );
}
